import {
  createSkyAuthoredBossArt,
  extractSkyAuthoredBossRig,
  skyAuthoredBossSpec,
  skyAuthoredAssetSpec,
  skyAuthoredPropSpec,
  skyAuthoredEnemySpec,
  verifySkyAuthoredBossAsset,
} from './220-sky-authored-bosses.mjs';
import {
  createSkyCathedralArt,
  extractSkyCathedralCollision,
  verifySkyCathedralAsset,
} from './222-sky-cathedral-art.mjs';
import { prepareSkyLeviathanReveal } from './163-sky-framed-view.mjs';
import { createSkyTextureBudget } from './173-sky-texture-budget.mjs';
import { createSkyLeviathanArt } from './162-sky-leviathan-art.mjs';
import {
  extractSkyLeviathanCollision,
  verifySkyLeviathanAsset,
} from './167-sky-leviathan-collision.mjs';
import { createSkyAssetCache } from './133-sky-asset-cache.mjs';
import {
  SKY_MODEL_FILES,
  SKY_OPTIONAL_MODEL_FILES,
  SKY_OPENING_ASSETS,
  skyBossAssetKey,
  skyStageAssets,
} from './134-sky-asset-manifest.mjs';
import {
  SKY_WORLD_ASSETS,
  skyBossWorldScale,
} from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { createSkyPilot } from './136-sky-pilot-animation.mjs';
import { createSkyDragonFactory } from './125-sky-dragon.mjs';
import { createSkyMegafauna, animateSkyMegafauna } from './124-sky-megafauna.mjs';
import { createGuardianArt } from './118-sky-guardian-art.mjs';
import { animateSkyArticulation } from './122-sky-articulation.mjs';

/** Own imported Blender resources per renderer. Clones share immutable geometry
 * and materials; animation clocks use domain active time, never a second RAF. */
export async function loadSkyModels(
  T,
  valid,
  { prototypeDragonURL, prototypeDragonRig, staged = false, signal } = {},
) {
  if (Boolean(prototypeDragonURL) !== Boolean(prototypeDragonRig))
    throw new Error('Prototype dragon model and rig must be supplied together.');
  if (
    prototypeDragonURL &&
    (typeof prototypeDragonURL !== 'string' ||
      !prototypeDragonURL.startsWith('/') ||
      prototypeDragonURL.startsWith('//') ||
      [...prototypeDragonURL].some(
        (character) => character.charCodeAt(0) <= 32 || character === '\\',
      ))
  )
    throw new Error('Prototype dragon requires an explicit same-origin model route.');
  let prototypeDragon, leviathanArt, cathedralArt;
  const authoredArt = new Map();
  const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
  const files = {
    ...SKY_MODEL_FILES,
    ...SKY_OPTIONAL_MODEL_FILES,
    ...(prototypeDragonURL ? { prototypeDragon: prototypeDragonURL } : {}),
  };
  const resources = new Set();
  const textureBudget = createSkyTextureBudget({ valid, signal });
  const cache = createSkyAssetCache({ load: loadAsset, valid, signal });
  const models = cache.values;
  const {
    ornamentGuardian,
    attachWeakpoint,
    prepareGuardian,
    releaseEnemy: releaseGuardian,
    updateDamage,
    updateIris,
  } = createGuardianArt(T, models, resources);
  function calibrateMaterial(material) {
    // The Blender studio key is softer than the sun/rim used in play. Keep
    // broad armor below bloom while retaining localized reactor highlights.
    if (material.name === 'weathered ceramic armour') {
      material.color.multiplyScalar(0.55);
      material.roughness = 0.57;
      material.metalness = 0.42;
      material.envMapIntensity = 0.6;
    } else if (material.name === 'brushed antique bronze') {
      material.roughness = 0.48;
      material.metalness = 0.7;
      material.envMapIntensity = 0.65;
    } else if (material.name === 'obsidian bio titanium') {
      material.roughness = 0.47;
      material.envMapIntensity = 0.7;
    } else if (material.name === 'ion teal') {
      material.emissiveIntensity = 0.65;
      material.roughness = 0.42;
    } else if (material.name === 'reactor coral') {
      material.emissiveIntensity = 1.75;
      material.roughness = 0.35;
    }
  }
  async function loadAsset(key, signal) {
    const sharedKey = { 'cinderheart-slag': 'rock', 'mourning-bride-echo': 'enemy-phantom-echo' }[
      key
    ];
    if (sharedKey) {
      await cache.ensure([sharedKey]);
      const source = models[sharedKey];
      return {
        value: { ...source, authoredRig: extractSkyAuthoredBossRig(T, source, key) },
        // The opening rock cache entry owns this shared source's GPU resources.
        dispose() {},
      };
    }
    if (!files[key]) throw new Error(`Unknown Sky artwork: ${key}`);
    const owned = new Set(),
      images = new Set();
    let released = false;
    function own(resource) {
      if (released) resource.dispose();
      else {
        owned.add(resource);
        resources.add(resource);
      }
    }
    function close() {
      if (released) return;
      released = true;
      for (const resource of owned) {
        resources.delete(resource);
        resource.dispose();
      }
      for (const image of images) textureBudget.releaseImage(image);
      owned.clear();
      images.clear();
    }
    const loader = new GLTFLoader();
    // Embedded images use img-src blob rather than a forbidden blob fetch.
    loader.register((parser) => ({
      name: 'MYAUDIENCE_embedded_images',
      loadTexture(index) {
        const source = parser.json.textures[index].source;
        if (source === undefined || parser.json.images[source].bufferView === undefined)
          return null;
        const imageLoader = new T.TextureLoader(parser.options.manager);
        const embedded = {
          load(url, done, progress, failed) {
            textureBudget
              .decode(
                () =>
                  new Promise((resolve, reject) =>
                    imageLoader.load(url, resolve, progress, reject),
                  ),
                signal,
              )
              .then(done, (error) => {
                URL.revokeObjectURL(url);
                failed(error);
              });
          },
        };
        return parser.loadTextureImage(index, source, embedded).then((texture) => {
          if (!texture) throw new Error('Sky Rider embedded artwork could not decode.');
          own(texture);
          if (texture.source?.data) {
            if (released) textureBudget.releaseImage(texture.source.data);
            else images.add(texture.source.data);
          }
          return texture;
        });
      },
    }));
    try {
      const mobile =
        textureBudget.maxDimension <= 1024 &&
        (key === 'cathedral' ||
          (Boolean(skyAuthoredAssetSpec(key)) && skyAuthoredAssetSpec(key).mobile !== false));
      const url =
        key === 'prototypeDragon'
          ? files[key]
          : `/assets/sky-rider/${files[key]}${mobile ? '-mobile' : ''}.glb`;
      const response = await fetch(url, {
        signal: AbortSignal.any([signal, AbortSignal.timeout(20000)]),
      });
      if (!response.ok) throw new Error(`Sky model unavailable: ${key}`);
      const bytes = await response.arrayBuffer();
      if (key === 'gildedLeviathan') await verifySkyLeviathanAsset(bytes);
      if (key === 'cathedral') await verifySkyCathedralAsset(bytes);
      if (skyAuthoredAssetSpec(key)) await verifySkyAuthoredBossAsset(bytes, key, mobile);
      if (key === 'rock') await verifySkyAuthoredBossAsset(bytes, 'cinderheart-slag');
      const gltf = await loader.parseAsync(bytes, '');
      gltf.scene.traverse((node) => {
        if (!node.isMesh) return;
        if (!owned.has(node.geometry)) own(node.geometry);
        if (node.isSkinnedMesh && !owned.has(node.skeleton)) own(node.skeleton);
        for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
          if (!owned.has(material)) {
            calibrateMaterial(material);
            own(material);
          }
          for (const value of Object.values(material))
            if (value?.isTexture && !owned.has(value)) own(value);
        }
        node.castShadow = key === 'rider';
        node.receiveShadow = true;
        node.frustumCulled = !node.isSkinnedMesh;
      });
      if (key === 'gildedLeviathan') {
        gltf.collision = extractSkyLeviathanCollision(T, gltf.scene);
        prepareSkyLeviathanReveal(gltf.collision);
      }
      if (key === 'cathedral') gltf.collision = extractSkyCathedralCollision(T, gltf.scene);
      if (skyAuthoredAssetSpec(key)) gltf.authoredRig = extractSkyAuthoredBossRig(T, gltf, key);
      return { value: gltf, dispose: close };
    } catch (error) {
      close();
      throw error;
    }
  }
  function dispose() {
    cathedralArt?.dispose();
    for (const factory of authoredArt.values()) factory.dispose();
    authoredArt.clear();
    prototypeDragon?.dispose();
    leviathanArt?.dispose();
    cache.dispose();
    textureBudget.dispose();
    for (const resource of resources) resource.dispose();
    resources.clear();
  }
  try {
    await cache.ensure(
      staged
        ? SKY_OPENING_ASSETS
        : [...Object.keys(SKY_MODEL_FILES), ...(prototypeDragonURL ? ['prototypeDragon'] : [])],
    );
    if (!valid()) {
      dispose();
      return null;
    }
  } catch (error) {
    dispose();
    if (!valid()) return null;
    throw error;
  }
  function preparePrototypeDragon() {
    if (!models.prototypeDragon || prototypeDragon) return;
    try {
      prototypeDragon = createSkyDragonFactory(T, models.prototypeDragon, prototypeDragonRig, {
        attachWeakpoint,
        prepareGuardian,
        updateDamage,
        updateIris,
        releaseEnemy: releaseGuardian,
      });
    } catch (error) {
      dispose();
      throw error;
    }
  }
  preparePrototypeDragon();
  leviathanArt = createSkyLeviathanArt(T, models, resources);
  function prepareAuthoredBosses() {
    if (models.cathedral && !cathedralArt)
      cathedralArt = createSkyCathedralArt(T, models.cathedral);
    for (const [key, gltf] of Object.entries(models))
      if (gltf.authoredRig && !authoredArt.has(key))
        authoredArt.set(key, createSkyAuthoredBossArt(T, gltf, gltf.authoredRig));
  }
  function releaseEnemy(group) {
    if (group.userData.skyCathedral) {
      cathedralArt?.release(group);
      return;
    }
    if (group.userData.skyAuthoredArt) {
      authoredArt.get(group.userData.skyAuthoredArt)?.release(group);
      return;
    }
    if (group.userData.skyLeviathan) {
      leviathanArt.release(group);
      return;
    }
    for (const skeleton of group.userData.megafaunaSkeletons ?? []) {
      resources.delete(skeleton);
      skeleton.dispose();
    }
    group.userData.megafaunaSkeletons?.clear();
    if (group.userData.prototypeDragon) prototypeDragon?.release(group);
    else releaseGuardian(group);
  }
  function clone(key) {
    const group = new T.Group();
    const scene =
      key === 'mammoth' ? cloneSkeleton(models[key].scene) : models[key].scene.clone(true);
    group.add(scene);
    if (key === 'mammoth') {
      const skeletons = new Set();
      scene.traverse((node) => {
        if (node.isSkinnedMesh) {
          skeletons.add(node.skeleton);
          resources.add(node.skeleton);
        }
      });
      group.userData.megafaunaSkeletons = skeletons;
    }
    group.userData.form = key;
    return group;
  }
  const rider = (options) => createSkyPilot(T, models.rider, options);
  function enemy(item) {
    if (item.kind === 'boss' && item.artVariant === 'cathedral-of-thunder') {
      if (!cathedralArt) throw new Error('Cathedral artwork is not ready.');
      return cathedralArt.create();
    }
    if (
      skyAuthoredAssetSpec(item.artVariant) &&
      (['boss', 'authored-prop'].includes(item.kind) ||
        (['drone', 'manta'].includes(item.kind) && skyAuthoredEnemySpec(item.artVariant)))
    ) {
      const factory = authoredArt.get(item.artVariant);
      if (!factory) throw new Error('Authored guardian artwork is not ready.');
      return factory.create();
    }
    if (item.kind === 'boss' && item.artVariant === 'gilded-leviathan')
      return leviathanArt.create();
    if (item.kind === 'boss' && item.artVariant === 'first-dragon') {
      if (!prototypeDragon) throw new Error('Prototype dragon model was not injected.');
      return prototypeDragon.enemy(item);
    }
    function captureParts(group) {
      group.userData.parts = [];
      group.userData.tentacles = [];
      group.traverse((node) => {
        if (node.isMesh && node.name.includes('tentacle')) {
          if (!models.nautilus.tentacleGeometry) {
            const geometry = node.geometry.clone(),
              source = geometry.attributes.position;
            geometry.morphAttributes.position = [-1, 1].map((direction) => {
              const values = new Float32Array(source.array);
              for (let i = 0; i < source.count; i++) {
                const x = source.getX(i),
                  y = source.getY(i);
                const weight = T.MathUtils.clamp((-y - 0.35) / 0.9, 0, 1) ** 2;
                values[i * 3] += direction * Math.sin(y * 4 + Math.abs(x) * 2) * 0.22 * weight;
                values[i * 3 + 2] += direction * Math.cos(y * 3) * 0.18 * weight;
              }
              return new T.BufferAttribute(values, 3);
            });
            resources.add(geometry);
            models.nautilus.tentacleGeometry = geometry;
          }
          node.geometry = models.nautilus.tentacleGeometry;
          node.updateMorphTargets();
          group.userData.tentacles.push(node);
        }
        if (
          !node.isMesh &&
          /vertebra|wing|jaw|orbit_|shell_|arm_|^head|^torso|^leg_|^turbine_|^tendril_/.test(
            node.name,
          )
        ) {
          group.userData.parts.push({
            node,
            base: node.rotation.clone(),
            position: node.position.clone(),
          });
        }
      });
      return group;
    }
    if (item.kind === 'pillar') {
      const group = clone('obelisk');
      group.children[0].scale.setScalar(0.5);
      group.children[0].position.y = -1.8;
      return group;
    }
    if (item.kind === 'bonus') return createSkyMegafauna(T, clone, item, resources);
    if (item.kind !== 'boss') {
      const key =
        item.enemyClass === 'bulwark'
          ? 'gunship'
          : item.enemyClass === 'ace'
            ? 'ace'
            : ['flanker', 'weaver'].includes(item.enemyClass) ||
                (!item.enemyClass && item.kind === 'manta')
              ? 'manta'
              : 'drone';
      // Actual Blender wing pivots retain authored airfoil topology; do not
      // cut arbitrary triangles out of the body to manufacture wing hinges.
      const group = clone(key);
      group.userData.craftForm = key;
      group.scale.setScalar(SKY_WORLD_ASSETS[key].modelScale * (item.size ?? 1));
      if (item.enemyClass === 'interceptor') {
        group.scale.x *= 0.72;
        group.scale.z *= 1.35;
      } else if (item.enemyClass === 'weaver') {
        group.scale.x *= 1.12;
        group.scale.z *= 0.82;
      }
      group.userData.guardianMaterials = [];
      group.traverse((node) => {
        if (!node.isMesh || node.material.name !== 'enemy gold cockpit aperture') return;
        node.material = node.material.clone();
        resources.add(node.material);
        group.userData.guardianMaterials.push(node.material);
        group.userData.cockpitMaterial = node.material;
        group.userData.cockpitGlow = node.material.emissiveIntensity;
      });
      return captureParts(group);
    }
    const profile = item.profile ?? '';
    const key = skyBossAssetKey(item);
    const group = new T.Group();
    group.userData.form = key;
    group.userData.profile = profile;
    if (key === 'oracle') group.userData.artVariant = 'tidal-oracle';
    const twins = profile === 'skeletalTwin';
    for (let i = 0; i < (twins ? 2 : 1); i++) {
      const model = clone(key);
      if (twins) {
        model.position.x = i ? 2.1 : -2.1;
        model.scale.setScalar(0.8);
      }
      model.userData.guardianCloneIndex = i;
      group.add(model);
    }
    group.scale.setScalar(skyBossWorldScale(profile, item.design, item.artVariant).modelScale);
    group.userData.orbiters = [];
    if (key === 'nautilus') {
      // Keep a central hittable core; satellites are bounded visual escorts.
      group.children[0].scale.setScalar(1);
      const satellites = profile === 'swarm' ? 4 : 2;
      for (let i = 0; i < satellites; i++) {
        const model = clone('nautilus');
        model.scale.setScalar(1.25);
        const phase = (i * Math.PI * 2) / satellites;
        model.position.set(Math.cos(phase) * 3.5, Math.sin(phase) * 2.8, -0.6);
        group.add(model);
        group.userData.orbiters.push({ model, phase });
      }
    }
    if (profile === 'solarTitan') {
      // Reuse only the authored dragon head, not another full serpent body.
      const source = models.leviathan.scene.getObjectByName('head');
      for (const side of [-1, 1]) {
        const escort = new T.Group();
        escort.add(source.clone(true));
        escort.scale.setScalar(0.75);
        escort.position.set(side * 3.8, -0.4, 1.2);
        group.add(escort);
      }
    }
    group.userData.design = item.design;
    ornamentGuardian(group);
    attachWeakpoint(group);
    prepareGuardian(group);
    return captureParts(group);
  }
  function animateEnemy(group, item, reduced, state) {
    if (group.userData.skyCathedral) {
      cathedralArt.animate(group, item, reduced, state);
      return;
    }
    if (group.userData.skyAuthoredArt) {
      authoredArt.get(group.userData.skyAuthoredArt).animate(group, item, reduced);
      return;
    }
    if (group.userData.skyLeviathan) {
      leviathanArt.animate(group, item, reduced, state);
      return;
    }
    if (group.userData.prototypeDragon) {
      prototypeDragon.animate(group, item, reduced);
      return;
    }
    if (item.kind === 'bonus') {
      animateSkyMegafauna(group, item, reduced);
      return;
    }

    const time = reduced ? 0 : (item.age ?? 0);
    const previous = group.userData.motion;
    const elapsed = previous ? (item.age ?? 0) - previous.age : 0;
    let vx = previous?.vx ?? 0,
      vy = previous?.vy ?? 0;
    if (elapsed > 0 && elapsed < 0.25) {
      vx = (item.x - previous.x) / elapsed;
      vy = (item.y - previous.y) / elapsed;
    }
    if (Number.isFinite(item.vx)) vx = item.vx;
    if (Number.isFinite(item.vy)) vy = item.vy;
    group.userData.motion = { x: item.x ?? 0, y: item.y ?? 0, age: item.age ?? 0, vx, vy };
    const boss = item.kind === 'boss';
    const cockpit = group.userData.cockpitMaterial;
    if (cockpit) {
      const duration = item.impactQuality === 'critical' ? 0.12 : 0.07;
      const sinceImpact = (item.age ?? 0) - (item.impactAt ?? -10);
      const flash =
        item.impactQuality !== 'armor' && sinceImpact >= 0
          ? Math.max(0, 1 - sinceImpact / duration)
          : 0;
      cockpit.emissive.set(flash > 0 && item.impactQuality === 'critical' ? '#fff3cf' : '#ffb946');
      cockpit.emissiveIntensity =
        group.userData.cockpitGlow +
        flash * (reduced ? 0.35 : 1.2) +
        T.MathUtils.clamp(item.charge ?? 0, 0, 1) * 0.55;
    }
    updateDamage(group, item, reduced);
    const charge = reduced ? 0 : T.MathUtils.clamp(item.charge ?? 0, 0, 1);
    const recoil = reduced ? 0 : T.MathUtils.clamp(item.recoil ?? 0, 0, 1);
    const bank = T.MathUtils.clamp(vx * -0.055, -0.24, 0.24);
    const forward = Math.max(6, item.vz ?? 17);
    const coordinatedBank = T.MathUtils.clamp(
      -Math.atan2(item.ax ?? 0, 9.81) * 1.3 + bank * 0.35,
      -0.32,
      0.32,
    );
    group.rotation.z = reduced ? 0 : boss ? bank * (1 - charge * 0.75) : coordinatedBank;
    group.rotation.x = reduced
      ? 0
      : boss
        ? T.MathUtils.clamp(vy * -0.03, -0.12, 0.12) - recoil * 0.045
        : T.MathUtils.clamp(-Math.atan2(vy, forward), -0.16, 0.16);
    if (boss)
      group.rotation.y = reduced
        ? 0
        : T.MathUtils.clamp(vx * 0.035, -0.22, 0.22) * (1 - charge * 0.7);
    if (!boss)
      group.rotation.y = reduced ? 0 : T.MathUtils.clamp(Math.atan2(vx, forward), -0.2, 0.2);
    for (const { model, phase } of group.userData.orbiters ?? []) {
      const angle = phase + time * 0.45,
        radius = 1 - charge * 0.15;
      model.position.set(
        Math.cos(angle) * 3.5 * radius,
        Math.sin(angle) * 2.8 * radius,
        -0.6 - charge * 0.35,
      );
    }
    for (const [index, tentacle] of (group.userData.tentacles ?? []).entries()) {
      tentacle.morphTargetInfluences[0] = reduced
        ? 0
        : (Math.sin(time * 2.7 + index * 0.8) + 1) * 0.45;
      tentacle.morphTargetInfluences[1] = reduced
        ? 0
        : (Math.cos(time * 2.1 + index * 0.8) + 1) * 0.4;
    }
    animateSkyArticulation(group, item, reduced, charge, recoil);
    updateIris(group, item, reduced);
  }
  return {
    textureUploadLimit: textureBudget.maxDimension,
    models,
    clone,
    rider,
    enemy,
    animateEnemy,
    releaseEnemy,
    dispose,
    encounterData: () => ({
      gildedLeviathan: models.gildedLeviathan?.collision ?? null,
      cathedral: models.cathedral?.collision ?? null,
      authoredEnemies: Object.fromEntries(
        Object.entries(models)
          .filter(([key, gltf]) => gltf.authoredRig && skyAuthoredEnemySpec(key))
          .map(([key, gltf]) => [key, gltf.authoredRig]),
      ),
      authoredProps: Object.fromEntries(
        Object.entries(models)
          .filter(([key, gltf]) => gltf.authoredRig && skyAuthoredPropSpec(key))
          .map(([key, gltf]) => [key, gltf.authoredRig]),
      ),
      authoredBosses: Object.fromEntries(
        Object.entries(models)
          .filter(([key, gltf]) => gltf.authoredRig && skyAuthoredBossSpec(key))
          .map(([key, gltf]) => [key, gltf.authoredRig]),
      ),
    }),
    prepareIndustrialWorld: () => cache.ensure(['refineryTower', 'refineryTowerLod']),
    prepareBriefing: async () => {
      if (!prototypeDragonURL) return;
      await cache.ensure(['prototypeDragon']);
      preparePrototypeDragon();
    },
    stageAssets: (state) => skyStageAssets(state, Boolean(prototypeDragonURL)),
    prepareStage: async (state) => {
      await cache.ensure(skyStageAssets(state, Boolean(prototypeDragonURL)).keys);
      preparePrototypeDragon();
      prepareAuthoredBosses();
    },
    stageReadiness: (state) =>
      cache.readiness(skyStageAssets(state, Boolean(prototypeDragonURL)).keys),
  };
}
